/**
 * The evolution builder, as pure data.
 *
 * Extracted from `apps/web/src/redux/evolutions/evolutionsBuilderSlice.ts`,
 * where the same rules live inside selectors that take the whole store. Nothing
 * here touches React or redux, so both apps derive identical sections and both
 * can be tested without a renderer.
 */
import { FACE_STAT_KEYS, INDIVIDUAL_ATTRIBUTE_KEYS, } from "../utils/playerAttributes";
import { includesIgnoringAccents } from "../utils/sbcListControls";
import { isPathwayEvolution, pathwayPathLabel } from "./pathway";
/**
 * Visual Upgrades leads, because a step that only changes the card art is the
 * one users most often want to dismiss first. Everything else keeps the order
 * the steps arrived in.
 */
const LEAD_CATEGORY = "Visual Upgrades";
/**
 * Joins a step to its catalogue entry. Expired evolutions are checked second,
 * so a chain referencing an evolution that has since dropped out of the live
 * list still resolves — flagged with `isExpired` so the UI can say so.
 */
export const describeStep = (step, evolutions, expiredEvolutions) => {
    const live = evolutions.find((evo) => evo.id === step.evolutionId);
    const expired = live ?? expiredEvolutions.find((evo) => evo.id === step.evolutionId);
    return {
        ...step,
        name: expired?.name ?? `Evolution ${step.evolutionId}`,
        rewardCategory: expired?.rewardCategories,
        isExpired: !live && !!expired,
        isPathway: expired ? isPathwayEvolution(expired) : false,
        pathwayLabel: expired
            ? pathwayPathLabel(expired, step.pathwayPath)
            : undefined,
        cost: expired?.cost,
        matches: expired?.matches,
        endTime: expired?.endTime,
    };
};
/**
 * The step list grouped into collapsible sections, in display order.
 *
 * Sections follow first-appearance order rather than a fixed list, so a
 * category with no available steps never renders an empty header.
 */
export const groupStepsByRewardCategory = (steps, evolutions, expiredEvolutions = [], 
/** Matches on the evolution's name; a category with no match is dropped. */
searchTerm = "") => {
    const term = searchTerm.trim();
    const described = steps
        .map((step) => describeStep(step, evolutions, expiredEvolutions))
        .filter((step) => !term || includesIgnoringAccents(step.name, term));
    const byCategory = new Map();
    for (const step of described) {
        const key = step.rewardCategory ?? "Other";
        const section = byCategory.get(key);
        if (section) {
            section.data.push(step);
        }
        else {
            byCategory.set(key, {
                category: step.rewardCategory,
                title: key,
                data: [step],
            });
        }
    }
    const sections = [...byCategory.values()];
    return sections.sort((a, b) => {
        if (a.category === LEAD_CATEGORY)
            return -1;
        if (b.category === LEAD_CATEGORY)
            return 1;
        return 0;
    });
};
/** Section titles carry their own count in the header. */
export const sectionHeaderLabel = (section) => `${section.title} (${section.data.length})`;
// ---------------------------------------------------------------------------
// Chain editing. The chain is an ordered list of evolution ids; everything else
// about the evolved player is computed server-side from it.
// ---------------------------------------------------------------------------
/**
 * Appends a step. Returns the chain unchanged when the step isn't among the
 * ones currently offered — the available set is recomputed after every change,
 * so anything else is a stale tap.
 */
export const appendStepToChain = (chain, evolutionId, availableSteps) => availableSteps.some((step) => step.evolutionId === evolutionId)
    ? [...chain, evolutionId]
    : chain;
/**
 * Drops the step at `index` and everything after it.
 *
 * Truncation rather than removal is deliberate: each step's availability is
 * computed against the ones before it, so pulling one out of the middle could
 * leave later steps that were never reachable without it.
 */
export const truncateChainAt = (chain, index) => chain.slice(0, Math.max(0, index));
export const clearChain = () => [];
// ---------------------------------------------------------------------------
// Deriving upgrades from the endpoint's response.
//
// The step endpoint returns a flat bag of deltas, not an upgrade list — the
// client builds it, here, for both apps.
// ---------------------------------------------------------------------------
/** Face-stat order of the `attributes` array, outfield and keeper. */
const FACE_STAT_LABELS = [
    "PACE",
    "SHOOTING",
    "PASSING",
    "DRIBBLING",
    "DEFENDING",
    "PHYSICALITY",
    "GOALKEEPING",
];
const FACE_STAT_LABELS_GK = [
    "DIVING",
    "HANDLING",
    "KICKING",
    "REFLEXES",
    "SPEED",
    "POSITIONING",
];
/** Keys of `caps`, positionally matching the `attributes` array. */
const FACE_STAT_CAP_KEYS = [
    "pace",
    "shooting",
    "passing",
    "dribblingTitle",
    "physicality",
    "defending",
];
/**
 * Turns one response step into the upgrade list the cards render.
 *
 * `isGk` only changes the face-stat labels — the six numbers in `attributes`
 * mean different things for a keeper.
 */
export const deriveStepUpgrades = (step, isGk = false) => {
    const upgrades = [];
    const capped = (key) => key && step.caps?.[key] === "partial-increase" ? true : undefined;
    if (step.skillMoves !== undefined) {
        upgrades.push({
            type: "skillmoves-upgrade",
            label: "SM",
            skillMoves: step.skillMoves,
            capped: capped("skillMoves"),
        });
    }
    if (step.weakFoot !== undefined) {
        upgrades.push({
            type: "weakfoot-upgrade",
            label: "WF",
            weakFoot: step.weakFoot,
            capped: capped("weakFoot"),
        });
    }
    if (step.metaRating?.delta) {
        upgrades.push({
            type: "meta-rating-upgrade",
            metaRating: step.metaRating,
            playerRoleId: step.playerRoleId,
        });
    }
    if (step.rating !== undefined) {
        upgrades.push({
            type: "ovr-upgrade",
            label: "OVR",
            value: step.rating,
            capped: capped("rating"),
        });
    }
    for (const playStyleId of step.playStylesPlus ?? []) {
        upgrades.push({ type: "play-styles-upgrade", playStyleId, isPlus: true });
    }
    for (const playStyleId of step.playStyles ?? []) {
        upgrades.push({ type: "play-styles-upgrade", playStyleId, isPlus: false });
    }
    const labels = isGk ? FACE_STAT_LABELS_GK : FACE_STAT_LABELS;
    const keys = isGk ? FACE_STAT_KEYS.gk : FACE_STAT_KEYS.outfield;
    step.attributes?.forEach((value, index) => {
        // Zero means "unchanged" — the response pads the array to full length.
        if (value > 0) {
            upgrades.push({
                type: "attribute-upgrade",
                id: index,
                attributeKey: keys[index] ?? "",
                label: labels[index]?.slice(0, 3) ?? "",
                value,
                capped: capped(FACE_STAT_CAP_KEYS[index]),
            });
        }
    });
    for (const attributeKey of INDIVIDUAL_ATTRIBUTE_KEYS) {
        const value = step[attributeKey];
        if (value === undefined)
            continue;
        upgrades.push({
            type: "in-game-attribute-upgrade",
            attributeKey,
            value,
            capped: capped(attributeKey),
        });
    }
    for (const roleId of step.rolesPlus ?? []) {
        upgrades.push({ type: "roles-plus-upgrade", roleId });
    }
    if (step.playerRoleId) {
        upgrades.push({
            type: "player-role-upgrade",
            playerRoleId: step.playerRoleId,
        });
    }
    if (step.accelerate) {
        upgrades.push({ type: "accelerate-upgrade", ...step.accelerate });
    }
    for (const position of step.possiblePositions ?? []) {
        upgrades.push({
            type: "position-upgrade",
            positionId: position,
            label: position,
        });
    }
    return upgrades;
};
/**
 * The tier the player moves to, or `undefined` when the step stays in band.
 *
 * Graded against the role the player ends up at: a step that changes the best
 * role is graded at the *new* role, since that is what the resulting card is.
 * Tier bands are always 0-chem, so the caller must pass 0-chem ratings.
 */
export const stepTierChange = ({ baseMetaRating, baseRoleId, stepMetaRating, stepRoleId, }, tierNameFor) => {
    if (baseMetaRating === undefined || baseRoleId === undefined)
        return undefined;
    const from = tierNameFor(baseMetaRating, baseRoleId);
    const to = tierNameFor(stepMetaRating, stepRoleId ?? baseRoleId);
    if (!from || !to || from === to)
        return undefined;
    return { from, to };
};
/**
 * Whether a step moves the player to a different best role.
 *
 * `playerRoleId` on a step is exactly that signal — the endpoint only sends it
 * when the evolution changes which role grades highest.
 */
export const stepChangesRole = (stepRoleId, currentRoleId) => stepRoleId !== undefined && stepRoleId !== 0 && stepRoleId !== currentRoleId;
/**
 * A `roles-plus-upgrade` role id above 100 is the "++" tier of the role at
 * id - 100; below it, the "+" tier of the role itself.
 */
export const rolePlusTier = (roleId) => ({
    roleId: roleId > 100 ? roleId - 100 : roleId,
    plusPlus: roleId > 100,
});
/** The mark a granted role carries on its chip: "+" or "++". */
export const rolePlusMark = (plusPlus) => (plusPlus ? "++" : "+");
export const groupUpgrades = (upgrades) => {
    const grouped = {
        attributes: [],
        inGameAttributes: [],
        positions: [],
        rolesPlus: [],
        skillMoves: [],
        weakFoot: [],
        playStyles: [],
        playStylesPlus: [],
        metaRating: null,
        ovr: null,
        playerRole: null,
        accelerate: null,
    };
    for (const upgrade of upgrades) {
        switch (upgrade.type) {
            case "attribute-upgrade":
                grouped.attributes.push(upgrade);
                break;
            case "in-game-attribute-upgrade":
                grouped.inGameAttributes.push(upgrade);
                break;
            case "position-upgrade":
                grouped.positions.push(upgrade);
                break;
            case "roles-plus-upgrade":
                grouped.rolesPlus.push(upgrade);
                break;
            case "skillmoves-upgrade":
                grouped.skillMoves.push(upgrade);
                break;
            case "weakfoot-upgrade":
                grouped.weakFoot.push(upgrade);
                break;
            case "play-styles-upgrade":
                (upgrade.isPlus ? grouped.playStylesPlus : grouped.playStyles).push(upgrade);
                break;
            case "meta-rating-upgrade":
                grouped.metaRating = upgrade;
                break;
            case "ovr-upgrade":
                grouped.ovr = upgrade;
                break;
            case "player-role-upgrade":
                grouped.playerRole = upgrade;
                break;
            case "accelerate-upgrade":
                grouped.accelerate = upgrade;
                break;
        }
    }
    return grouped;
};
/** The at-a-glance view of `groupUpgrades`, as the mobile card draws it. */
export const summariseUpgrades = (upgrades) => {
    const grouped = groupUpgrades(upgrades);
    return {
        stats: [...(grouped.ovr ? [grouped.ovr] : []), ...grouped.attributes]
            .filter((upgrade) => upgrade.value)
            .map(({ label, value }) => ({ label, value })),
        playStyles: [...grouped.playStylesPlus, ...grouped.playStyles].map(({ playStyleId, isPlus }) => ({ playStyleId, isPlus })),
        roles: grouped.rolesPlus.map((upgrade) => upgrade.roleId),
        skillMoves: grouped.skillMoves.at(-1)?.skillMoves,
        weakFoot: grouped.weakFoot.at(-1)?.weakFoot,
        positions: grouped.positions.map((upgrade) => upgrade.label),
    };
};
/**
 * Positions the chain could still unlock, and the evolutions that would do it.
 *
 * An evolution can grant a position, and with it every role under that
 * position — roles the card cannot be graded at today. The builder's role
 * picker lists them separately from the ones the player already has, so
 * choosing one is a deliberate "grade this build as if it ends up there"
 * rather than something that looks available now.
 *
 * Read off the offered steps rather than the evolutions catalogue: the steps
 * are already filtered to what this player, this chain and this include-list
 * allow, so a position only appears when it is genuinely reachable.
 */
export const potentialPositionRoles = ({ steps, playerRoles, positionsOrder, currentPositions, }) => {
    const current = new Set(currentPositions);
    // Insertion-ordered so the evolutions read in the order the list offers them.
    const rewardedBy = new Map();
    for (const step of steps) {
        for (const upgrade of step.upgrades) {
            if (upgrade.type !== "position-upgrade")
                continue;
            if (current.has(upgrade.positionId))
                continue;
            const names = rewardedBy.get(upgrade.positionId) ?? new Set();
            names.add(step.name);
            rewardedBy.set(upgrade.positionId, names);
        }
    }
    return (positionsOrder
        .filter((position) => rewardedBy.has(position))
        .map((position) => ({
        position,
        roles: playerRoles.filter((role) => role.position === position),
        rewardedBy: [...(rewardedBy.get(position) ?? [])],
    }))
        // A position whose roles we don't know is nothing to offer.
        .filter((group) => group.roles.length > 0));
};
/**
 * The PlayStyles to draw beside a step or a chain: the ones it grants, then the
 * ones the player already has.
 *
 * Showing only what a step grants hides whether the player owned the playstyle
 * already, which is the difference between an evolution being worth its cost
 * and being wasted. The two groups are kept apart rather than merged so the
 * caller can box the gained ones.
 */
export const splitPlayStyles = ({ gained, basePlayStyles = [], basePlayStylesPlus = [], }) => {
    // A step that promotes a PlayStyle the player already has to a plus would
    // otherwise render it twice — once gained, once existing — reading as two
    // separate PlayStyles. The gained copy is the informative one.
    const promoted = new Set(gained.filter((playStyle) => playStyle.isPlus).map((p) => p.playStyleId));
    return {
        gained,
        existingPlus: basePlayStylesPlus,
        existingSingle: basePlayStyles.filter((id) => !promoted.has(id)),
    };
};
/** The meta-rating this step would move the player to, when it reports one. */
export const stepMetaRatingDelta = (upgrades) => upgrades.find((upgrade) => upgrade.type === "meta-rating-upgrade")?.metaRating;
/** True when a step grants nothing the UI would render. */
export const isEmptyUpgrade = (upgrades) => {
    const summary = summariseUpgrades(upgrades);
    return (summary.stats.length === 0 &&
        summary.playStyles.length === 0 &&
        summary.roles.length === 0 &&
        summary.positions.length === 0 &&
        summary.skillMoves === undefined &&
        summary.weakFoot === undefined);
};
// ---------------------------------------------------------------------------
// Pathway drafts — "Choose your pathway" before a pathway step joins the chain.
// ---------------------------------------------------------------------------
/**
 * The neighbor entry for a pathway, if the solver offered one. Neighbors
 * carry their pathway in `pathwayPath`; a pathway with no entry cannot be
 * chosen from the current one.
 */
export const pathwayNeighborFor = (neighbors, pathway) => {
    const key = pathway.join();
    return neighbors.find((neighbor) => neighbor.pathwayPath?.join() === key);
};
/**
 * Whether `option` at `level` can be chosen from `pathway`: it is the current
 * choice, or one option away with a neighbor to back it.
 */
export const isPathwayOptionAvailable = (pathway, neighbors, level, option) => pathway[level] === option ||
    !!pathwayNeighborFor(neighbors, pathway.map((chosen, l) => (l === level ? option : chosen)));
/** A draft for a pathway step picked from the list, on the solver's path. */
export const createPathwayDraft = (step) => ({
    gridStep: step,
    pathway: step.pathwayPath ?? [],
    selectedStep: step,
    neighbors: [],
    loading: false,
});
/**
 * Moves the draft to `pathway`. Unchanged while neighbors load, when the
 * pathway is already chosen, or when the solver offered no entry for it.
 * The old neighbors are dropped and the draft reads as loading until the
 * host has fetched the new ones.
 */
export const selectDraftPathway = (draft, pathway) => {
    if (draft.loading || draft.pathway.join() === pathway.join())
        return draft;
    const neighbor = pathwayNeighborFor(draft.neighbors, pathway);
    if (!neighbor)
        return draft;
    const recommended = draft.gridStep.pathwayPath ?? [];
    return {
        ...draft,
        pathway,
        selectedStep: pathway.join() === recommended.join() ? draft.gridStep : neighbor,
        neighbors: [],
        loading: true,
    };
};
// ---------------------------------------------------------------------------
// Chain recommendation tags.
// ---------------------------------------------------------------------------
/** Solver tags in display order; the "best-N-step" ones sort last. */
export const CHAIN_TAG_ORDER = [
    "last-chance",
    "free",
    "low-effort",
    "best-silver",
    "best-bronze",
    "high-potential",
    "5-star-5-star",
    "gullit",
    "nainggolan",
    "best-1-step",
    "best-2-step",
    "best-3-step",
    "best-4-step",
    "best-5-step",
    "best-6-step",
    "best-7-step",
    "best-8-step",
    "best-9-step",
    "best-10-step",
];
/** The tags at least one chain carries, in display order. */
export const availableChainTags = (chains) => {
    const present = new Set(chains.flatMap((chain) => chain.tags ?? []));
    return CHAIN_TAG_ORDER.filter((tag) => present.has(tag));
};
/** English display text for a tag; hosts with i18n map the key themselves. */
export const chainTagText = (tag) => {
    const step = /^best-(\d+)-step$/.exec(tag);
    if (step)
        return `BEST ${step[1]} STEP`;
    const labels = {
        "last-chance": "LAST CHANCE",
        free: "FREE",
        "low-effort": "LOW EFFORT",
        "best-silver": "BEST SILVER",
        "best-bronze": "BEST BRONZE",
        "high-potential": "HIGH POTENTIAL",
        "5-star-5-star": "5★ 5★",
        gullit: "GULLIT GANG",
        nainggolan: "NAINGGOLAN GANG",
    };
    return labels[tag] ?? tag.toUpperCase().replaceAll("-", " ");
};
export const chainTagTone = (tag) => {
    if (/^best-\d+-step$/.test(tag))
        return "best-step";
    switch (tag) {
        case "last-chance":
            return "urgency";
        case "free":
        case "low-effort":
            return "accessibility";
        case "best-silver":
            return "silver";
        case "best-bronze":
            return "bronze";
        case "high-potential":
            return "potential";
        case "5-star-5-star":
            return "stars";
        case "gullit":
            return "gullit";
        case "nainggolan":
            return "nainggolan";
        default:
            return "best-step";
    }
};
/** Chains best first, narrowed to a search term and a tag pick. */
export const sortAndFilterChains = (chains, { searchTerm = "", selectedTags = [], } = {}) => {
    const term = searchTerm.trim();
    return chains
        .filter((chain) => !term || includesIgnoringAccents(chain.chainName ?? "", term))
        .filter((chain) => selectedTags.length === 0 ||
        chain.tags?.some((tag) => selectedTags.includes(tag)))
        .sort((a, b) => b.metaRating - a.metaRating);
};
//# sourceMappingURL=builder.js.map